/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Switch, Typography } from '@mui/material';
import { ClientGroupExtendedAccessibilityEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import ClickInfoTooltipView from '../../../shared/views/ClickInfoTooltip.view';

const OPEN_TYPE_MESSAGE = 'Der Inhalt der Gruppe ist für alle Nutzer der Anwendung sichtbar.';
const PRIVATE_TYPE_MESSAGE = 'Inhalt nach außen sichtbar.';

interface Props {
	groupContentVisibility: boolean;
	groupType: ClientGroupExtendedAccessibilityEnum | null;
	handleGroupTypeChange: (event: React.ChangeEvent<{ value: string }>) => void;
	handleGroupContentVisibilityChange: (event: React.ChangeEvent<{ checked: boolean }>) => void;
}

const GroupTypeView = (props: Props): JSX.Element => {
	const { groupType, groupContentVisibility, handleGroupTypeChange, handleGroupContentVisibilityChange } = props;
	return (
		<>
			<FormControl variant="standard">
				<FormLabel>
					Gruppentyp
					<ClickInfoTooltipView>
						<Box width={250}>
							<Box>
								<Typography variant="body2" style={{ color: 'white' }}>
									Öffentlich
								</Typography>
							</Box>
							<Box>
								<Typography variant="body2" style={{ color: 'white' }}>
									Jeder Nutzer kann der Gruppe beitreten. Der Inhalt ist für alle sichtbar.
								</Typography>
							</Box>
							<Box pt={1}>
								<Typography variant="body2" style={{ color: 'white' }}>
									Privat
								</Typography>
							</Box>
							<Box>
								<Typography variant="body2" style={{ color: 'white' }}>
									Nutzer können nur durch Administratoren eingeladen werden.
								</Typography>
							</Box>
						</Box>
					</ClickInfoTooltipView>
				</FormLabel>
				<RadioGroup id="groupType" name="groupType" value={groupType} onChange={handleGroupTypeChange} row>
					<FormControlLabel
						value={ClientGroupExtendedAccessibilityEnum.Public}
						control={<Radio />}
						label="Öffentlich"
					/>
					<FormControlLabel
						value={ClientGroupExtendedAccessibilityEnum.ApprovalRequired}
						control={<Radio />}
						label="Privat"
					/>
				</RadioGroup>
			</FormControl>
			<Box width={1}>
				{groupType ? (
					<Typography component={'span'} variant="body2">
						{groupType === ClientGroupExtendedAccessibilityEnum.Public ? (
							<Box height="30px" pt={1}>
								{OPEN_TYPE_MESSAGE}
							</Box>
						) : (
							<Box height="30px">
								{PRIVATE_TYPE_MESSAGE}
								<Switch
									checked={groupContentVisibility}
									value={groupContentVisibility}
									onChange={handleGroupContentVisibilityChange}
									color="primary"
									name="groupContentVisibility"
									id="groupContentVisibility"
									inputProps={{ 'aria-label': 'primary checkbox' }}
								/>
							</Box>
						)}
					</Typography>
				) : (
					<></>
				)}
			</Box>
		</>
	);
};

export default GroupTypeView;
